import { Divider, Spin } from 'antd';
import { Empty, Form, Input, Modal, Select, Tree, Button } from '@maxtropy/components';
import type { DataNode } from 'antd/es/tree';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import styles from '../BindUnitModal/index.module.scss';
import _ from 'lodash';
import { EnergyUnitTreeItem, getEnergyUnitTree } from '@/api/energyUnitMapping';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { apiV2EnergyAnalysisListByOuPost, V2EnergyAnalysisListPostResponse } from '@maxtropy/device-customer-apis-v2';

export type AnalysisUnit = Exclude<V2EnergyAnalysisListPostResponse['list'], undefined>[number];

export interface SelectedEnergyUnit {
  energyUnitId: number;
  energyUnitName: string;
  energyUnitGroupId: number;
  energyUnitGroupName: string;
}

interface UnitDataNode extends DataNode {
  parentId: string;
  label?: string;
}

function formatToNodeData(data: EnergyUnitTreeItem[]): UnitDataNode[] {
  return data.map(i => {
    return {
      title: i.name,
      label: i.name,
      key: i.energyUnitId,
      children: i.children && i.children.length > 0 ? formatToNodeData(i.children) : [],
      disabled: i.disabled,
      parentId: i.parentId,
    };
  });
}

const dataList: { key: React.Key; title: string }[] = [];

const generateList = (data: DataNode[]) => {
  for (let i = 0; i < data.length; i++) {
    const node = data[i];
    const { key, title } = node;
    dataList.push({ key, title: title as string });
    if (node.children) {
      generateList(node.children);
    }
  }
};

const getParentKey = (key: React.Key, tree: DataNode[]): React.Key => {
  let parentKey: React.Key;
  for (let i = 0; i < tree.length; i++) {
    const node = tree[i];
    if (node.children) {
      if (node.children.some(item => item.key === key)) {
        parentKey = node.key;
      } else if (getParentKey(key, node.children)) {
        parentKey = getParentKey(key, node.children);
      }
    }
  }
  return parentKey!;
};

const getParentIdsByNodeId = (data: DataNode[], nodeId: React.Key, path: React.Key[] = []): React.Key[] => {
  for (const node of data) {
    const id = node.key;
    path.push(node.key);
    if (id === nodeId) return path;
    if (node.children) {
      const findChildren = getParentIdsByNodeId(node.children, nodeId, path);
      if (findChildren?.length) return findChildren;
    }
    path.pop();
  }
  return [];
};

const getIds = (data: DataNode, ids: React.Key[]) => {
  if (!data.disabled) {
    ids.push(data.key);
  }
  if (data.children) {
    for (const child of data.children) {
      getIds(child, ids);
    }
  }
  return ids;
};

interface Props {
  open: boolean;
  setOpen: (v: boolean) => void;
  isEdit: boolean;
  unitGroup: AnalysisUnit[];
  existingUnits: SelectedEnergyUnit[]; // 已存在的用能单元，用于置灰
  onConfirm: (units: SelectedEnergyUnit[]) => void;
}

const BindEnergyUnitModal: React.FC<Props> = ({ open, setOpen, isEdit, unitGroup, existingUnits, onConfirm }) => {
  const [innerForm] = Form.useForm();
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>();
  const [searchValue, setSearchValue] = useState('');
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [treeNodeData, setTreeNodeData] = useState<UnitDataNode[]>([]);
  const innerEnergyGroupId = Form.useWatch('energyGroupId', innerForm);
  const [loading, setLoading] = useState<boolean>(false);
  const [unitCheckedKeys, setUnitCheckedKeys] = useState<React.Key[]>([]);

  useEffect(() => {
    if (open) {
      setUnitCheckedKeys([]);
      setSearchValue('');
      dataList.length = 0;
      if (unitGroup?.length > 0) {
        innerForm.setFieldValue('energyGroupId', unitGroup[0].id);
      }
    }
  }, [open, unitGroup]);

  const unitGroupOption = useMemo(() => {
    if (unitGroup?.length > 0) {
      return unitGroup?.map(i => ({
        label: i.name,
        value: i.id,
      }));
    }
    return [];
  }, [unitGroup]);

  const existingUnitIds = useMemo(() => {
    return existingUnits.map(u => String(u.energyUnitId));
  }, [existingUnits]);

  const checkAllChildren = (data: DataNode) => {
    const unitIds = getIds(data, []);
    setUnitCheckedKeys(prev => {
      const newKeys = unitIds.filter(id => !existingUnitIds.includes(String(id))) as React.Key[];
      return [...prev, ...newKeys];
    });
  };

  const uncheckAllChildren = (data: DataNode) => {
    const unitIds = getIds(data, []);
    setUnitCheckedKeys(prev => prev.filter(key => !unitIds.includes(key)));
  };

  const treeData = useMemo(() => {
    const loop = (data: UnitDataNode[]): DataNode[] =>
      data.map(item => {
        const strTitle = item.title as string;
        const index = strTitle.indexOf(searchValue);
        const beforeStr = strTitle.substring(0, index);
        const afterStr = strTitle.slice(index + searchValue.length);
        const isExisting = existingUnitIds.includes(String(item.key));

        let title: ReactNode =
          index > -1 ? (
            <span>
              {beforeStr}
              <span className={styles.siteTreeSearchValue}>{searchValue}</span>
              {afterStr}
            </span>
          ) : (
            <span>{strTitle}</span>
          );

        if ((item.parentId as unknown) === 0 && getIds(item, []).length > 1) {
          title = (
            <>
              {title}
              <Button
                disabled={item.disabled || isExisting}
                type="link"
                size="small"
                onClick={() => checkAllChildren(item)}
              >
                全选子节点
              </Button>
              <Button onClick={() => uncheckAllChildren(item)} type="link" size="small">
                取消全选子节点
              </Button>
            </>
          );
        }

        if (item.children) {
          return {
            title,
            key: item.key,
            children: loop(item.children as UnitDataNode[]),
            disabled: item.disabled || isExisting,
            selectable: true,
          };
        }
        return {
          title,
          key: item.key,
          disabled: item.disabled || isExisting,
          selectable: true,
        };
      });

    return loop(treeNodeData);
  }, [searchValue, treeNodeData, existingUnitIds]);

  const onExpand = (newExpandedKeys: React.Key[]) => {
    setExpandedKeys(newExpandedKeys);
    setAutoExpandParent(false);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const newExpandedKeys = dataList
      .map(item => {
        if (item.title.indexOf(value) > -1) {
          return getParentKey(item.key, treeNodeData);
        }
        return null;
      })
      .filter((item, i, self) => item && self.indexOf(item) === i);
    const allParentKeys = (newExpandedKeys as React.Key[]).map(key => {
      return getParentIdsByNodeId(treeNodeData, key);
    });
    setExpandedKeys(_.uniq(allParentKeys.flat()));
    setSearchValue(value);
    setAutoExpandParent(true);
  };

  const onCheck = (keys: any) => {
    setUnitCheckedKeys(keys.checked);
  };

  useEffect(() => {
    if (innerEnergyGroupId) {
      setLoading(true);
      dataList.length = 0;
      getEnergyUnitTree(innerEnergyGroupId)
        .then(res => {
          const nodes = formatToNodeData(res.list ?? []);
          setTreeNodeData(nodes);
          generateList(nodes);
          setExpandedKeys(dataList.map(i => i.key));
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setTreeNodeData([]);
      dataList.length = 0;
    }
  }, [innerEnergyGroupId]);

  const onBind = () => {
    const energyGroupId = innerForm.getFieldValue('energyGroupId');
    const groupInfo = unitGroup?.find(i => i.id === energyGroupId);

    const selectedUnits: SelectedEnergyUnit[] = unitCheckedKeys.map(item => {
      const found = dataList.find(i => String(i.key) === String(item));
      return {
        energyUnitId: Number(item),
        energyUnitName: found ? found.title : '',
        energyUnitGroupId: energyGroupId,
        energyUnitGroupName: groupInfo?.name || '',
      };
    });

    onConfirm(selectedUnits);
    setOpen(false);
    innerForm.resetFields();
  };

  return (
    <Modal
      title="关联用能单元"
      open={open}
      onCancel={() => {
        setOpen(false);
        innerForm.resetFields();
      }}
      onOk={onBind}
    >
      <Form form={innerForm}>
        <Form.Item label="用能分析组" name="energyGroupId">
          <Select placeholder="请选择" options={unitGroupOption} />
        </Form.Item>
        <div className={styles.content}>
          <Form.Item
            extra={
              treeData.length > 0 && (
                <p>
                  <ExclamationCircleOutlined style={{ color: 'var(--warning-color)' }} />
                  灰色选择框表示无该用能单元权限或已被添加
                </p>
              )
            }
          >
            <div>
              <Input.Search placeholder="请输入关键字" onChange={onInputChange} />
              <Divider style={{ borderColor: 'rgba(255,255,255,0.15)' }} />

              <Spin spinning={loading}>
                {treeData.length > 0 ? (
                  <div className={styles.treeBackground} style={{ overflow: 'auto', maxHeight: 367, maxWidth: 433 }}>
                    <Tree
                      checkable
                      checkStrictly
                      checkedKeys={unitCheckedKeys}
                      onExpand={onExpand}
                      expandedKeys={expandedKeys}
                      autoExpandParent={autoExpandParent}
                      treeData={treeData}
                      onCheck={onCheck}
                    />
                  </div>
                ) : (
                  <Empty style={{ maxWidth: 433 }} />
                )}
              </Spin>
            </div>
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default BindEnergyUnitModal;
